import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Row,
  Col,
  DatePicker,
  Select,
  Divider,
  UploadFile,
  FormInstance,
} from "antd";
import ImageUpload from "../../../Common/ImageUpload";
import { useTranslation } from "react-i18next";
import { UserCreateParams } from "../../../../hooks/api/users";
import { UserRoleType, UserStatusType } from "../../../../store/auth";
import dayjs, { Dayjs } from "dayjs";
import { enumToOptionsArray } from "../../../../utils";
import { UserPlanDisplay, UserRoleDisplay } from "../../../../enums/user";

export interface UserFormValues {
  fullName: string;
  email: string;
  role: UserRoleType;
  phone: string;
  avatar?: string;
  plan?: string;
  birthdate?: Dayjs | null;
  gender?: string;
  address?: string;
  country?: string;
  city?: string;
  zipCode?: string;
  // status?: UserStatusType;
}

export interface UsersFormProps {
  initialValues?: UserFormValues;
  form?: FormInstance<UserFormValues>;
  onSubmit?: (values: UserFormValues) => void | Promise<void>;
  fileList?: UploadFile[];
  setFileList: React.Dispatch<React.SetStateAction<UploadFile[]>>;
}

export default function UsersForm({
  // form,
  initialValues,
  fileList,
  setFileList,
  onSubmit,
}: UsersFormProps): React.ReactElement {
  // const [form] = Form.useForm<UserFormValues>();
  const form = Form.useFormInstance();
  // const [fileList, setFileList] = useState<UploadFile[]>([]);
  const { t } = useTranslation();

  // const handleFinish = async (values: UserFormValues) => {
  //   try {
  //     const payload: UserFormValues = {
  //       ...values,
  //       avatar: fileList ? fileList[0]?.url : "",
  //     };
  //     await onSubmit(payload);
  //   } finally {
  //     // setLoading(false);
  //   }
  // };
  const mainFields = [
    {
      name: "fullName",
      label: t("pages.users.form.name"),
      required: true,
      component: <Input />,
      rules: [
        {
          required: true,
        },
        {
          min: 3,
        },
        {
          pattern: /^[A-Za-zÀ-ž\s'-]+$/,
        },
      ],
    },
    {
      name: "email",
      label: t("pages.users.form.email"),
      required: true,
      component: <Input placeholder={t("pages.users.form.enterPlaceholder")} />,
      rules: [
        {
          required: true,
        },
        {
          type: "email",
        },
      ],
    },
    {
      name: "phone",
      label: t("pages.users.form.phoneNumber"),
      component: <Input placeholder={t("pages.users.form.enterPlaceholder")} />,
    },
    {
      name: "role",
      label: t("pages.users.form.role"),
      required: true,
      component: (
        <Select
          placeholder="Select"
          options={enumToOptionsArray(UserRoleDisplay)}
        />
      ),
    },
    {
      name: "plan",
      label: t("pages.users.form.plan"),
      required: true,
      component: (
        <Select
          placeholder={t("pages.users.form.selectPlaceholder")}
          options={enumToOptionsArray(UserPlanDisplay)}
        />
      ),
    },
  ];

  const personalInfoFields = [
    {
      name: "birthdate",
      label: t("pages.users.form.birthDate"),
      component: (
        <DatePicker
          style={{ width: "100%" }}
          disabledDate={(current) => current && current > dayjs().endOf("day")}
        />
      ),
    },
    {
      name: "gender",
      label: t("pages.users.form.gender"),
      component: (
        <Select
          placeholder={t("pages.users.form.selectPlaceholder")}
          options={[
            { value: "male", label: t("pages.users.form.male") },
            { value: "female", label: t("pages.users.form.female") },
            { value: "other", label: t("pages.users.form.other") },
          ]}
        />
      ),
    },
    {
      name: "address",
      label: t("pages.users.form.address"),
      component: <Input placeholder={t("pages.users.form.enterPlaceholder")} />,
    },
    {
      name: "city",
      label: t("pages.users.form.city"),
      component: <Input placeholder={t("pages.users.form.enterPlaceholder")} />,
    },
    {
      name: "country",
      label: t("pages.users.form.country"),
      component: <Input placeholder={t("pages.users.form.enterPlaceholder")} />,
    },
    {
      name: "zipCode",
      label: t("pages.users.form.zipCode"),
      component: <Input placeholder={t("pages.users.form.enterPlaceholder")} />,
    },
  ];

  const renderFields = (fields: any[]) =>
    fields.map((f) => (
      <Col span={12} key={f.name}>
        <Form.Item name={f.name} label={f.label} rules={f.rules}>
          {f.component}
        </Form.Item>
      </Col>
    ));

  return (
    <>
      <Row gutter={24}>
        <Col span={4}>
          <ImageUpload
            form={form}
            name="avatar"
            fileList={fileList || []}
            setFileList={setFileList}
          />
        </Col>

        <Col span={18}>
          <Row gutter={16}>{renderFields(mainFields)}</Row>
        </Col>
      </Row>

      <Divider orientation="horizontal" titlePlacement="left">
        {t("pages.users.form.personalInformation")}
      </Divider>

      <Row gutter={16}>
        <Col span={4} />
        <Col span={18}>
          <Row gutter={16}>{renderFields(personalInfoFields)}</Row>
        </Col>
      </Row>
    </>
  );
}
